/**
 * A row of the utilities table, as the app reads it.
 *
 * `ami_status` is the data route. Five values are current (see
 * src/lib/utilityRoutes.ts); the two legacy values, supported and unsupported,
 * stay legal in the database until the Phase 1 client is promoted, so every
 * reader goes through normalizeAmiStatus() rather than comparing the raw text.
 */
export type AmiStatus = 'email_request' | 'self_download' | 'customer_request' | 'connect' | 'none';

export interface UtilityRow {
  id: string;
  name: string;
  state: string;
  ami_contact_email: string | null;
  ami_status: string;
  ami_notes: string | null;
  is_active?: boolean | null;
  notes?: string | null;
  /** The customer portal the homeowner signs in to. */
  portal_url: string | null;
  /** One step per line. */
  download_steps: string | null;
  /** What the portal exports, in words: "Green Button XML or CSV". */
  export_format: string | null;
  /** How many months of interval history the portal offers. Null when unknown. */
  history_months: number | null;
  /** Shown when 12 months of smart-meter history may not exist yet. */
  rollout_note: string | null;
  /** True once a real homeowner confirmed the steps matched what they saw. */
  instructions_verified: boolean;
  instructions_verified_at?: string | null;
  /** The utility help page the steps were written from. */
  instructions_source_url: string | null;
  /**
   * customer_request form route: the utility's request-form URL with
   * {placeholders}, filled by src/lib/requestForm.ts. Null: the customer emails
   * ami_contact_email instead.
   */
  request_form_template?: string | null;
  /** Latest homeowner note explaining how the steps differed. */
  instructions_feedback?: string | null;
  eia_id?: number | null;
  residential_customers?: number | null;
  /** Every state the utility serves; null means only `state`. */
  states_served?: string[] | null;
  created_at?: string;
  updated_at?: string | null;
}

/** The columns the Service Info dropdown needs. Kept light: the list is national. */
export type UtilityListItem = Pick<
  UtilityRow,
  'id' | 'name' | 'state' | 'states_served' | 'ami_status' | 'ami_contact_email' | 'residential_customers'
>;

/** Every column the dropdown reads, as a PostgREST select list. */
export const UTILITY_LIST_COLUMNS = 'id, name, state, states_served, ami_status, ami_contact_email, residential_customers';

/** True when the utility serves the state, by home state or by the served list. */
export function utilityServesState(u: Pick<UtilityRow, 'state' | 'states_served'>, state: string): boolean {
  const code = state.toUpperCase();
  if ((u.state ?? '').toUpperCase() === code) return true;
  return (u.states_served ?? []).some(s => (s ?? '').toUpperCase() === code);
}
